import { useCallback, useEffect, useRef, useState } from "react";
import {
  createPublicClient,
  createWalletClient,
  custom,
  getAddress,
  http,
  parseAbi,
} from "viem";
import { arbitrum } from "viem/chains";

/**
 * useArbitrumWallet — injected-wallet (EIP-1193) hook for the Strategy
 * Automation tab, patterned on the viem usage in SignalsTab.js.
 *
 * Scope is deliberately narrow: connect, watch account/chain, switch to
 * Arbitrum, personal_sign the enrollment challenge (off-chain, free, grants
 * nothing), and REVOKE the trading delegate (removeTradingDelegate — the
 * safety/off switch).
 *
 * The enabling ON-CHAIN actions (setTradingDelegate, USDC approve) are
 * intentionally NOT implemented here: onboarding stays in the existing
 * supervised flow, so nothing in this tab can start live trading. signMessage
 * is not such an action — a personal_sign cannot move funds or grant an
 * allowance; the only state it changes is server-side enrollment.
 *
 * All signing happens in the member's own browser wallet. No keys ever leave it.
 */

export const ARBITRUM_CHAIN_ID = 42161;

const DIAMOND_ABI = parseAbi(["function removeTradingDelegate()"]);

export default function useArbitrumWallet() {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(null); // 'revoke' | null
  const walletClientRef = useRef(null);
  const publicClientRef = useRef(null);

  const hasInjected = typeof window !== "undefined" && !!window.ethereum;
  const chainOk = chainId === ARBITRUM_CHAIN_ID;

  const getPublicClient = useCallback(() => {
    if (publicClientRef.current) return publicClientRef.current;
    try {
      publicClientRef.current = createPublicClient({
        chain: arbitrum,
        transport: hasInjected ? custom(window.ethereum) : http(),
      });
    } catch {
      publicClientRef.current = createPublicClient({ chain: arbitrum, transport: http() });
    }
    return publicClientRef.current;
  }, [hasInjected]);

  /* connect via the injected wallet; returns the checksummed address */
  const connect = useCallback(async () => {
    if (!hasInjected) {
      throw new Error(
        "No browser wallet detected. Install MetaMask (or another EVM wallet) and reload."
      );
    }
    setConnecting(true);
    try {
      const wc = createWalletClient({ chain: arbitrum, transport: custom(window.ethereum) });
      walletClientRef.current = wc;
      const [addr] = await wc.requestAddresses();
      const cid = await wc.getChainId();
      const checksummed = getAddress(addr);
      setAccount(checksummed);
      setChainId(cid);
      return checksummed;
    } finally {
      setConnecting(false);
    }
  }, [hasInjected]);

  /* react to wallet account / chain changes */
  useEffect(() => {
    if (!hasInjected) return undefined;
    const eth = window.ethereum;
    const onAccounts = (accts) =>
      setAccount(accts && accts.length ? getAddress(accts[0]) : null);
    const onChain = (cidHex) => setChainId(parseInt(cidHex, 16));
    eth.on?.("accountsChanged", onAccounts);
    eth.on?.("chainChanged", onChain);
    return () => {
      eth.removeListener?.("accountsChanged", onAccounts);
      eth.removeListener?.("chainChanged", onChain);
    };
  }, [hasInjected]);

  /**
   * Open the wallet's own account picker and return whichever address the
   * member chooses (ARC-46).
   *
   * Adding a second wallet needs a signature from THAT wallet, and only the
   * member can decide which one it is — so we ask the extension to re-prompt
   * (`wallet_requestPermissions` is what reopens MetaMask's picker;
   * `eth_requestAccounts` silently returns the current selection once a site is
   * already connected, which would sign the wrong address without ever asking).
   *
   * A wallet that doesn't implement the permissions RPC falls back to reading
   * the current selection — the member switches accounts in the extension
   * first, and the panel tells them so. Either way the address we return is the
   * one that will be asked to sign, and the member sees it before signing.
   */
  const requestAccountSelection = useCallback(async () => {
    if (!hasInjected) {
      throw new Error(
        "No browser wallet detected. Install MetaMask (or another EVM wallet) and reload."
      );
    }
    const eth = window.ethereum;
    try {
      await eth.request?.({
        method: "wallet_requestPermissions",
        params: [{ eth_accounts: {} }],
      });
    } catch (e) {
      // 4001 is the member closing the picker — that IS their answer, so it
      // propagates as a rejection rather than falling through to whichever
      // account happened to be selected already.
      if (e?.code === 4001) throw e;
      /* Anything else: the wallet has no such method. Fall through. */
    }
    const wc =
      walletClientRef.current ||
      createWalletClient({ chain: arbitrum, transport: custom(eth) });
    walletClientRef.current = wc;
    const [addr] = await wc.requestAddresses();
    if (!addr) throw new Error("No account was selected in your wallet.");
    const checksummed = getAddress(addr);
    setAccount(checksummed);
    return checksummed;
  }, [hasInjected]);

  const switchToArbitrum = useCallback(async () => {
    const wc = walletClientRef.current;
    if (!wc) return;
    try {
      await wc.switchChain({ id: arbitrum.id });
      setChainId(ARBITRUM_CHAIN_ID);
    } catch (e) {
      // 4902 = chain not added to the wallet yet.
      if (e?.code === 4902 || /Unrecognized chain/i.test(e?.message || "")) {
        await wc.addChain({ chain: arbitrum });
        await wc.switchChain({ id: arbitrum.id });
        setChainId(ARBITRUM_CHAIN_ID);
      } else {
        throw e;
      }
    }
  }, []);

  /**
   * personal_sign the server-built enrollment message (SIWE step 2).
   *
   * FREE and off-chain: this is a signature, not a transaction. It costs no
   * gas, moves nothing, and grants no allowance or trading permission — it only
   * proves to the engine that this browser controls the address. Trading
   * permission is a separate, explicit on-chain setTradingDelegate that stays
   * in the supervised onboarding flow, not here.
   *
   * The message is signed VERBATIM as the server built it. We never
   * reconstruct, reformat, or append to it — a client-rebuilt SIWE message is
   * how signature-replay and phishing bugs get in.
   *
   * ARC-46: `address` names WHICH wallet is being asked. Enrollment omits it
   * and signs with the connected account, as before. Adding a second wallet
   * passes the address the member picked, so the message bound to that address
   * is the one it signs — signing a challenge issued for a different wallet
   * would be rejected by the engine anyway, and asking for it would be asking
   * the member to prove the wrong thing.
   */
  const signMessage = useCallback(
    async (message, address) => {
      const wc = walletClientRef.current;
      const signer = address || account;
      if (!wc || !signer) throw new Error("Connect your wallet first.");
      if (typeof message !== "string" || !message) {
        throw new Error("No signing message was provided by the engine.");
      }
      setBusy("sign");
      try {
        return await wc.signMessage({ account: signer, message });
      } finally {
        setBusy(null);
      }
    },
    [account]
  );

  /**
   * Revoke the trading delegate on the gTrade diamond. Always available —
   * stops automated trading immediately; the member's funds are unaffected.
   */
  const revokeDelegate = useCallback(
    async (diamond) => {
      const wc = walletClientRef.current;
      if (!wc || !account) throw new Error("Connect your wallet first.");
      if (!chainOk) throw new Error("Switch to the Arbitrum network first.");
      if (!diamond) throw new Error("gTrade diamond address unavailable — backend not reachable yet.");
      setBusy("revoke");
      try {
        const hash = await wc.writeContract({
          address: getAddress(diamond),
          abi: DIAMOND_ABI,
          functionName: "removeTradingDelegate",
          args: [],
          account,
          chain: arbitrum,
        });
        await getPublicClient().waitForTransactionReceipt({ hash });
        return hash;
      } finally {
        setBusy(null);
      }
    },
    [account, chainOk, getPublicClient]
  );

  return {
    account,
    chainId,
    chainOk,
    hasInjected,
    connecting,
    busy,
    connect,
    requestAccountSelection,
    switchToArbitrum,
    signMessage,
    revokeDelegate,
  };
}
